import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, getUserRoles, getPaymentTypes, getLocale } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../components/ui/pagination";
import { Users as UsersIcon, Plus, Pencil, Trash2, Search, Shield, KeyRound, X, Camera, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Users = () => {
  const { t, i18n } = useTranslation();
  const { getErrorMessage, translateMessage } = useApiMessages();
  const { user: currentUser, isAdmin, isRevendedor, canManageCompanies } = useAuth();
  
  // Recalcular locale cuando cambie el idioma
  const currentLocale = useMemo(() => getLocale(), [i18n.language]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [workSchedules, setWorkSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "vigilante",
    company_id: "",
    hourly_rate: 0,
    fixed_salary: 0,
    payment_type: "hourly",
    pay_lunch_hours: false,
    company_limit: null,
    photo_base64: null,
    work_schedule_id: null,
  });
  
  // Estados para la cámara y foto
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Estados para el modal de foto grande
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const fetchWorkSchedules = async () => {
    try {
      const schedulesRes = await axios.get(`${API_URL}/work-schedules`, { headers: getAuthHeaders() });
      const schedulesData = Array.isArray(schedulesRes.data) ? schedulesRes.data : [];
      console.log("Fetched work schedules:", schedulesData);
      console.log("Work schedules count:", schedulesData.length);
      setWorkSchedules(schedulesData);
      return schedulesData;
    } catch (error) {
      console.error("Error fetching work schedules:", error);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      // Construir URL con filtro de compañía si es necesario
      let usersUrl = `${API_URL}/users`;
      if ((isAdmin() || isRevendedor()) && selectedCompanyId !== "all") {
        usersUrl += `?company_id=${selectedCompanyId}`;
      }
      
      const [usersRes, companiesRes] = await Promise.all([
        axios.get(usersUrl, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
      ]);
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
      console.log("Fetched users:", usersData);
      const revendedorUsers = usersData.filter(u => u.role === "revendedor");
      console.log("Revendedor users:", revendedorUsers);
      revendedorUsers.forEach(u => {
        console.log(`User ${u.name} (${u.id}): company_limit =`, u.company_limit, "type:", typeof u.company_limit);
      });
      setUsers(usersData);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      
      // Cargar horarios siempre
      await fetchWorkSchedules();
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("pages.users.errorLoadingData"));
      setUsers([]);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCompanyId]);

  // Recargar horarios cuando se abre el formulario y cambia el company_id
  useEffect(() => {
    if (isFormOpen && formData.role === "vigilante") {
      console.log("useEffect: Recargando horarios, company_id:", formData.company_id, "isFormOpen:", isFormOpen);
      fetchWorkSchedules();
    }
  }, [isFormOpen, formData.company_id, formData.role]);

  const handleOpenForm = async (user = null) => {
    console.log("handleOpenForm called, user:", user ? "EDIT" : "NEW");
    
    if (user) {
      // Buscar el usuario actualizado en la lista para obtener los datos más recientes
      const updatedUser = users.find(u => u.id === user.id) || user;
      console.log("Opening form for user (EDIT):", updatedUser);
      console.log("user.company_id:", updatedUser.company_id, "type:", typeof updatedUser.company_id);
      console.log("user.work_schedule_id:", updatedUser.work_schedule_id);
      setSelectedUser(updatedUser);
      
      // Asegurar que company_limit sea un número o null
      let companyLimitValue = null;
      if (user.company_limit !== undefined && user.company_limit !== null && user.company_limit !== "") {
        const parsed = typeof user.company_limit === "string" ? parseInt(user.company_limit, 10) : Number(user.company_limit);
        companyLimitValue = !isNaN(parsed) && parsed > 0 ? parsed : null;
      }
      console.log("Setting company_limit to:", companyLimitValue, "type:", typeof companyLimitValue);
      
      const newFormData = {
        email: updatedUser.email,
        password: "",
        name: updatedUser.name,
        role: updatedUser.role,
        company_id: updatedUser.company_id,
        hourly_rate: updatedUser.hourly_rate || 0,
        fixed_salary: updatedUser.fixed_salary || 0,
        payment_type: updatedUser.payment_type || "hourly",
        pay_lunch_hours: updatedUser.pay_lunch_hours || false,
        company_limit: companyLimitValue,
        photo_base64: updatedUser.photo_base64 || null,
        work_schedule_id: updatedUser.work_schedule_id !== undefined && updatedUser.work_schedule_id !== null && updatedUser.work_schedule_id !== "" ? updatedUser.work_schedule_id : null,
      };
      console.log("Setting work_schedule_id from user:", updatedUser.work_schedule_id, "->", newFormData.work_schedule_id);
      setFormData(newFormData);
      setCapturedPhoto(updatedUser.photo_base64 ? `data:image/jpeg;base64,${updatedUser.photo_base64}` : null);
      console.log("FormData set for EDIT, company_id:", newFormData.company_id);
      console.log("FormData work_schedule_id:", newFormData.work_schedule_id);
      
      // Recargar horarios inmediatamente después de establecer el formData
      // Esto asegura que los horarios se carguen con el company_id correcto
      await fetchWorkSchedules();
      console.log("Horarios recargados después de establecer formData para EDIT");
      console.log("workSchedules disponibles:", workSchedules.length);
      console.log("formData.work_schedule_id:", newFormData.work_schedule_id);
      // Verificar si el horario asignado está en la lista
      if (newFormData.work_schedule_id) {
        const scheduleExists = workSchedules.find(s => s.id === newFormData.work_schedule_id);
        console.log("Horario asignado existe en lista:", scheduleExists ? "SÍ" : "NO");
      }
    } else {
      console.log("Opening form for NEW user");
      setSelectedUser(null);
      setFormData({
        email: "",
        password: "",
        name: "",
        role: "vigilante",
        company_id: canManageCompanies() ? "" : currentUser.company_id,
        hourly_rate: 0,
        fixed_salary: 0,
        payment_type: "hourly",
        pay_lunch_hours: false,
        company_limit: null,
        photo_base64: null,
        work_schedule_id: null,
      });
      setCapturedPhoto(null);
    }
    
    // Abrir el formulario primero
    setIsFormOpen(true);
    
    // Recargar horarios DESPUÉS de establecer el formData
    // Usar un pequeño delay para asegurar que el formData se haya actualizado
    setTimeout(async () => {
      console.log("Recargando horarios después de abrir formulario...");
      const schedules = await fetchWorkSchedules();
      console.log("Schedules loaded after form open, count:", schedules.length);
      console.log("Current formData.company_id:", formData.company_id);
    }, 200);
  };

  const startCamera = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }, // Cámara frontal
        audio: false,
      });
      
      streamRef.current = stream;
      setCapturing(true);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
      
    } catch (err) {
      console.error("Camera error:", err);
      toast.error(t("pages.users.cameraError", { message: err.message }));
    }
  }, [t]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      toast.error(t("pages.users.videoNotAvailable"));
      return;
    }
    
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    
    if (width === 0 || height === 0) {
      toast.error(t("pages.users.waitForCamera"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);
    
    const photoData = canvas.toDataURL("image/jpeg", 0.7);
    setCapturedPhoto(photoData);
    
    // Extraer solo la parte base64 (sin el prefijo data:image/jpeg;base64,)
    const base64Data = photoData.split(',')[1];
    setFormData({ ...formData, photo_base64: base64Data });
    
    // Stop camera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCapturing(false);
    toast.success(t("pages.users.photoCaptured"));
  }, [formData, t]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    setFormData({ ...formData, photo_base64: null });
    startCamera();
  }, [formData, startCamera]);

  const handleFilePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar que sea una imagen
    if (!file.type.startsWith('image/')) {
      toast.error(t("pages.users.invalidImageFile"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const photoData = event.target?.result?.toString();
      if (photoData) {
        setCapturedPhoto(photoData);
        // Extraer solo la parte base64
        const base64Data = photoData.split(',')[1];
        setFormData({ ...formData, photo_base64: base64Data });
        toast.success(t("pages.users.photoUploaded"));
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setCapturedPhoto(null);
    setFormData({ ...formData, photo_base64: null });
  };

  useEffect(() => {
    return () => {
      // Limpiar la cámara al desmontar
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validar que los vigilantes tengan un horario asignado
      if (formData.role === "vigilante" && !formData.work_schedule_id) {
        toast.error(t("pages.users.workScheduleRequired"));
        return;
      }

      if (selectedUser) {
        const updateData = { ...formData };
        delete updateData.password;
        delete updateData.email;
        // Solo incluir company_limit si el rol es revendedor
        if (updateData.role !== "revendedor") {
          delete updateData.company_limit;
        } else {
          // Si es revendedor, asegurar que company_limit se envíe (incluso si es null)
          if (updateData.company_limit === "" || updateData.company_limit === undefined) {
            updateData.company_limit = null;
          }
        }
        // Si photo_base64 es null, no enviarlo (para no sobrescribir la foto existente)
        if (updateData.photo_base64 === null) {
          delete updateData.photo_base64;
        }
        // Asegurar que work_schedule_id se envíe incluso si es null (para poder eliminar el horario)
        // Convertir cadena vacía a null
        if (updateData.work_schedule_id === undefined || updateData.work_schedule_id === "") {
          updateData.work_schedule_id = null;
        }
        console.log("Sending update request with data:", updateData);
        console.log("updateData.work_schedule_id:", updateData.work_schedule_id, "type:", typeof updateData.work_schedule_id);
        const response = await axios.put(`${API_URL}/users/${selectedUser.id}`, updateData, {
          headers: getAuthHeaders(),
        });
        console.log("Update response:", response.data);
        console.log("Update response work_schedule_id:", response.data.work_schedule_id);
        toast.success(t("api.success.userUpdated"));
        // Actualizar el usuario en la lista localmente para que se refleje inmediatamente
        setUsers(prevUsers => prevUsers.map(u => 
          u.id === selectedUser.id ? { ...u, ...response.data } : u
        ));
      } else {
        const createData = { ...formData };
        // Solo incluir company_limit si el rol es revendedor
        if (createData.role !== "revendedor") {
          delete createData.company_limit;
        } else {
          // Si es revendedor, asegurar que company_limit se envíe (incluso si es null)
          if (createData.company_limit === "" || createData.company_limit === undefined) {
            createData.company_limit = null;
          }
        }
        // Asegurar que work_schedule_id se envíe incluso si es null
        if (createData.work_schedule_id === undefined) {
          createData.work_schedule_id = null;
        }
        console.log("Sending create request with data:", createData);
        await axios.post(`${API_URL}/users`, createData, {
          headers: getAuthHeaders(),
        });
        toast.success(t("api.success.userCreated"));
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.saveError"));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/users/${selectedUser.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t("api.success.userDeleted"));
      setIsDeleteOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.deleteError"));
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    
    setResettingPassword(true);
    try {
      const response = await axios.post(
        `${API_URL}/users/${resetPasswordUser.id}/reset-password`,
        {},
        { headers: getAuthHeaders() }
      );
      
      console.log("Reset password response:", response.data);
      
      // El backend siempre devuelve temporary_password (puede ser null si no es temporal)
      const tempPassword = response.data?.temporary_password;
      console.log("Temporary password received:", tempPassword);
      
      if (tempPassword) {
        setTemporaryPassword(tempPassword);
        // Asegurar que el diálogo permanezca abierto
        setIsResetPasswordOpen(true);
        toast.success(t("api.success.passwordReset"));
      } else {
        // Si no hay contraseña temporal, cerrar el diálogo
        toast.success(t("api.success.passwordReset"));
        setIsResetPasswordOpen(false);
        setResetPasswordUser(null);
        setTemporaryPassword(null);
      }
    } catch (error) {
      console.error("Error al resetear contraseña:", error);
      toast.error(getErrorMessage(error, "api.errors.unknownError"));
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredUsers = Array.isArray(users) ? users.filter(
    (u) =>
      u?.name?.toLowerCase().includes(search.toLowerCase()) ||
      u?.email?.toLowerCase().includes(search.toLowerCase())
  ) : [];

  // Paginación
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [search, selectedCompanyId]);

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-700",
      revendedor: "bg-orange-100 text-orange-700",
      supervisor: "bg-blue-100 text-blue-700",
      vigilante: "bg-green-100 text-green-700",
    };
    const labels = {
      admin: t("roles.admin"),
      revendedor: t("roles.revendedor"),
      supervisor: t("roles.supervisor"),
      vigilante: t("roles.vigilante"),
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[role]}`}>
        {labels[role]}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="users-page" key={i18n.language}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{t("pages.users.title")}</h1>
          <p className="text-slate-600 mt-1">{t("pages.users.subtitle")}</p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t("pages.users.newUser")}
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("pages.users.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-10 ${search ? "pr-10" : ""}`}
                data-testid="search-users"
              />
              {search && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-slate-100"
                  onClick={() => setSearch("")}
                  title={t("pages.users.clearSearch")}
                >
                  <X className="h-4 w-4 text-slate-400" />
                </Button>
              )}
            </div>
            
            {/* Company Filter - Solo para Admin y Revendedor */}
            {(isAdmin() || isRevendedor()) && (
              <div className="w-full sm:w-[250px]">
                <Label className="text-sm text-slate-600 mb-1 block">{t("pages.users.filterByCompany")}</Label>
                <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("pages.users.allCompanies")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.users.allCompanies")}</SelectItem>
                    {Array.isArray(companies) && companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Items per page selector */}
            <div className="w-full sm:w-auto">
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.users.perPage")}</Label>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(parseInt(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            {t("pages.users.usersFound", { count: filteredUsers.length })}
          </p>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pages.users.user")}</TableHead>
                <TableHead>{t("pages.users.role")}</TableHead>
                <TableHead>{t("pages.users.paymentType")}</TableHead>
                <TableHead>{t("pages.users.hourlyRate")}</TableHead>
                <TableHead>{t("pages.users.fixedSalary")}</TableHead>
                <TableHead>{t("pages.users.workSchedule")}</TableHead>
                <TableHead>{t("pages.users.status")}</TableHead>
                <TableHead className="text-right">{t("pages.users.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    {t("pages.users.noUsers")}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {user.photo_base64 ? (
                          <img
                            src={`data:image/jpeg;base64,${user.photo_base64}`}
                            alt={user.name}
                            className="w-10 h-10 rounded-full object-cover border-2 border-slate-300 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              setSelectedPhoto({
                                photo: user.photo_base64,
                                name: user.name
                              });
                              setIsPhotoModalOpen(true);
                            }}
                          />
                        ) : (
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-slate-600">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-slate-600 capitalize">
                      {user.payment_type === "hourly" ? t("pages.users.hourly") : t("pages.users.mixed")}
                    </TableCell>
                    <TableCell className="font-mono">
                      ${user.hourly_rate?.toFixed(2) || "0.00"}
                    </TableCell>
                    <TableCell className="font-mono">
                      ${user.fixed_salary ? user.fixed_salary.toLocaleString(currentLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
                    </TableCell>
                    <TableCell>
                      {user.role === "vigilante" && user.work_schedule_id ? (
                        (() => {
                          const schedule = workSchedules.find(s => s.id === user.work_schedule_id);
                          return schedule ? (
                            <div className="flex items-center gap-2">
                              {schedule.color && (
                                <span
                                  className="w-3 h-3 rounded-full border border-slate-300"
                                  style={{ backgroundColor: schedule.color }}
                                />
                              )}
                              <span className="text-sm">
                                {schedule.name || t("pages.workSchedules.defaultScheduleName")}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">{t("pages.users.noSchedule")}</span>
                          );
                        })()
                      ) : user.role === "vigilante" ? (
                        <span className="text-sm text-slate-400">{t("pages.users.noSchedule")}</span>
                      ) : (
                        <span className="text-sm text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.is_active
                            ? "bg-green-100 text-green-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {user.is_active ? t("pages.users.active") : t("pages.users.inactive")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenForm(user)}
                          data-testid={`edit-user-${user.id}`}
                          title={t("pages.users.editUser")}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => {
                            setResetPasswordUser(user);
                            setTemporaryPassword(null);
                            setIsResetPasswordOpen(true);
                          }}
                          data-testid={`reset-password-user-${user.id}`}
                          title={t("pages.users.resetPassword")}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(user);
                            setIsDeleteOpen(true);
                          }}
                          data-testid={`delete-user-${user.id}`}
                          title={t("pages.users.deleteUser")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        {totalPages > 1 && (
          <div className="border-t p-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Mostrar primera página, última página, página actual y páginas adyacentes
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <PaginationItem key={page}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return null;
                })}
                
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
            <div className="text-center text-sm text-slate-500 mt-2">
              {t("pages.users.showingUsers", { 
                start: startIndex + 1, 
                end: Math.min(endIndex, filteredUsers.length), 
                total: filteredUsers.length 
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? t("pages.users.editUserTitle") : t("pages.users.newUserTitle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Columna izquierda: Foto y botones */}
              <div className="space-y-4">
                {/* Foto del usuario */}
                <div className="space-y-2">
                  <Label>{t("pages.users.userPhoto")}</Label>
                  <div className="flex flex-col items-center gap-3">
                    {capturedPhoto ? (
                      <div className="relative">
                        <img
                          src={capturedPhoto}
                          alt={t("pages.users.userPhoto")}
                          className="w-32 h-32 rounded-full object-cover border-2 border-slate-300 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedPhoto({
                              photo: formData.photo_base64,
                              name: formData.name || t("pages.users.user")
                            });
                            setIsPhotoModalOpen(true);
                          }}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white"
                          onClick={removePhoto}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-slate-200 flex items-center justify-center border-2 border-slate-300">
                        <UsersIcon className="h-16 w-16 text-slate-400" />
                      </div>
                    )}
                    <div className="flex flex-col gap-2 w-full">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setIsPhotoDialogOpen(true);
                          startCamera();
                        }}
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        {t("pages.users.capturePhoto")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {t("pages.users.uploadPhoto")}
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFilePhoto}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Horario Semanal - Solo para vigilantes */}
                {formData.role === "vigilante" && (
                  <div className="space-y-2">
                    <Label htmlFor="work_schedule_id">{t("pages.users.workSchedule")} *</Label>
                    <Select
                      key={`work-schedule-select-${formData.company_id}-${workSchedules.length}-${formData.work_schedule_id || "none"}`}
                      value={formData.work_schedule_id && formData.work_schedule_id !== "" ? formData.work_schedule_id : "none"}
                      onValueChange={(value) => {
                        const newValue = value === "none" ? null : value;
                        console.log("Select onValueChange:", value, "->", newValue);
                        setFormData({ ...formData, work_schedule_id: newValue });
                      }}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("pages.users.selectWorkSchedule")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("pages.users.noSchedule")}</SelectItem>
                        {(() => {
                          console.log("=== DEBUG WORK SCHEDULES SELECTOR ===");
                          console.log("Total work schedules available:", workSchedules.length);
                          console.log("All work schedules:", workSchedules);
                          console.log("Form company_id:", formData.company_id, "type:", typeof formData.company_id);
                          console.log("Form role:", formData.role);
                          console.log("Is editing:", !!selectedUser);
                          
                          const filteredSchedules = workSchedules.filter(schedule => {
                            // Mostrar solo horarios activos
                            if (!schedule.is_active) {
                              console.log(`Schedule "${schedule.name || schedule.id}" filtered out (inactive)`);
                              return false;
                            }
                            
                            // Si hay una compañía seleccionada, filtrar por ella
                            if (formData.company_id) {
                              // Si el horario no tiene company_id (null), mostrarlo para todas las compañías
                              if (!schedule.company_id || schedule.company_id === null || schedule.company_id === "") {
                                console.log(`Schedule "${schedule.name || schedule.id}" included (no company_id, available for all)`);
                                return true;
                              }
                              
                              // Comparar como strings para evitar problemas de tipo
                              const scheduleCompanyId = String(schedule.company_id);
                              const formCompanyId = String(formData.company_id);
                              const matches = scheduleCompanyId === formCompanyId;
                              
                              console.log(`Schedule "${schedule.name || schedule.id}":`, {
                                schedule_company_id: scheduleCompanyId,
                                form_company_id: formCompanyId,
                                matches: matches
                              });
                              
                              if (!matches) {
                                console.log(`Schedule "${schedule.name || schedule.id}" filtered out (company mismatch)`);
                              }
                              return matches;
                            }
                            
                            // Si no hay compañía seleccionada, mostrar todos los horarios activos
                            console.log(`Schedule "${schedule.name || schedule.id}" included (no company filter)`);
                            return true;
                          });
                          console.log("Filtered schedules count:", filteredSchedules.length);
                          console.log("Filtered schedules:", filteredSchedules);
                          console.log("=== END DEBUG ===");
                          
                          return filteredSchedules.map((schedule) => (
                            <SelectItem key={schedule.id} value={schedule.id}>
                              <div className="flex items-center gap-2">
                                {schedule.color && (
                                  <span
                                    className="w-3 h-3 rounded-full border border-slate-300"
                                    style={{ backgroundColor: schedule.color }}
                                  />
                                )}
                                <span>{schedule.name || t("pages.workSchedules.defaultScheduleName")}</span>
                              </div>
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {t("pages.users.workScheduleDescription")}
                    </p>
                  </div>
                )}
              </div>

              {/* Columna derecha: Resto de campos */}
              <div className="md:col-span-2 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("pages.users.name")} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="user-name-input"
                  />
                </div>

                {!selectedUser && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t("common.email")} *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="user-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">{t("common.password")} *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        data-testid="user-password-input"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>{t("pages.users.role")} *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Supervisor y Revendedor no pueden asignar roles de admin o revendedor */}
                      {getUserRoles(t).filter((role) => 
                        isAdmin() ? true : !["admin", "revendedor"].includes(role.value)
                      ).map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Admin y Revendedor ven selector de compañía, supervisor usa su compañía automáticamente */}
                {canManageCompanies() ? (
                  <div className="space-y-2">
                    <Label>{t("nav.companies")} *</Label>
                    <Select
                      value={formData.company_id}
                      onValueChange={(value) => {
                        setFormData({ 
                          ...formData, 
                          company_id: value,
                          // Limpiar horario si no pertenece a la nueva compañía
                          work_schedule_id: formData.work_schedule_id && workSchedules.find(s => s.id === formData.work_schedule_id)?.company_id === value 
                            ? formData.work_schedule_id 
                            : null
                        });
                      }}
                    >
                      <SelectTrigger data-testid="user-company-select">
                        <SelectValue placeholder={t("pages.companies.selectCompany")} />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.isArray(companies) && companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                {/* Campo para límite de compañías (solo para revendedores) */}
                {formData.role === "revendedor" && isAdmin() && (
                  <div className="space-y-2">
                    <Label htmlFor="company_limit">{t("pages.users.companyLimit")}</Label>
                    <Input
                      id="company_limit"
                      type="number"
                      min="1"
                      step="1"
                      value={formData.company_limit !== null && formData.company_limit !== undefined && formData.company_limit !== "" ? String(formData.company_limit) : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        console.log("Input onChange - value:", value, "type:", typeof value);
                        setFormData({
                          ...formData,
                          company_limit: value && value.trim() !== "" ? parseInt(value, 10) : null,
                        });
                      }}
                      placeholder={t("pages.users.noLimit")}
                      data-testid="user-company-limit-input"
                    />
                    <p className="text-xs text-slate-500">
                      {t("pages.users.companyLimitDescription")}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t("pages.users.paymentType")}</Label>
                  <Select
                    value={formData.payment_type}
                    onValueChange={(value) => {
                      // Si cambia a "hourly", limpiar fixed_salary
                      // Si cambia a "mixed", mantener ambos valores
                      setFormData({
                        ...formData,
                        payment_type: value,
                        fixed_salary: value === "hourly" ? 0 : formData.fixed_salary,
                      });
                    }}
                  >
                    <SelectTrigger data-testid="user-payment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getPaymentTypes(t).map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Campos de pago - mostrar según el tipo seleccionado */}
                {formData.payment_type === "hourly" ? (
                  // Solo mostrar tarifa/hora si es "Por hora"
                  <div className="space-y-2">
                    <Label htmlFor="hourly_rate">{t("pages.users.hourlyRateRequired")}</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.hourly_rate}
                      onChange={(e) =>
                        setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })
                      }
                      data-testid="user-hourly-rate-input"
                    />
                  </div>
                ) : (
                  // Mostrar ambos campos si es "Mixto"
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="hourly_rate">{t("pages.users.hourlyRateLabel")}</Label>
                      <Input
                        id="hourly_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.hourly_rate}
                        onChange={(e) =>
                          setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })
                        }
                        data-testid="user-hourly-rate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="fixed_salary">{t("pages.users.fixedSalaryLabel")}</Label>
                      <Input
                        id="fixed_salary"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.fixed_salary}
                        onChange={(e) =>
                          setFormData({ ...formData, fixed_salary: parseFloat(e.target.value) || 0 })
                        }
                        data-testid="user-fixed-salary-input"
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="pay_lunch_hours"
                    checked={formData.pay_lunch_hours}
                    onChange={(e) => setFormData({ ...formData, pay_lunch_hours: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    data-testid="user-pay-lunch-checkbox"
                  />
                  <Label htmlFor="pay_lunch_hours" className="text-sm font-medium cursor-pointer">
                    {t("pages.users.payLunchHours")}
                  </Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" data-testid="save-user-btn">
                {selectedUser ? t("common.saveChanges") : t("pages.users.createUser")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <AlertDialog open={isResetPasswordOpen} onOpenChange={(open) => {
        setIsResetPasswordOpen(open);
        if (!open) {
          setResetPasswordUser(null);
          setTemporaryPassword(null);
        }
      }}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.users.resetPassword")}</AlertDialogTitle>
            <AlertDialogDescription>
              {temporaryPassword ? (
                <div className="space-y-4">
                  <p>
                    {t("pages.users.passwordResetSuccess", { name: resetPasswordUser?.name })}
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-blue-900 mb-2">
                      {t("pages.users.temporaryPassword")}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border border-blue-300 font-mono text-lg font-bold text-blue-700 break-all select-all">
                        {temporaryPassword || t("common.notAvailable")}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(temporaryPassword);
                          toast.success(t("api.success.passwordCopied"));
                        }}
                      >
                        {t("pages.users.copy")}
                      </Button>
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      {t("pages.users.sharePasswordMessage")}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {t("pages.users.confirmResetPassword", { name: resetPasswordUser?.name })}
                  <p className="text-sm text-slate-500 mt-2">
                    {t("pages.users.temporaryPasswordInfo")}
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {temporaryPassword ? (
              <AlertDialogAction
                onClick={() => {
                  setIsResetPasswordOpen(false);
                  setResetPasswordUser(null);
                  setTemporaryPassword(null);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t("pages.users.close")}
              </AlertDialogAction>
            ) : (
              <>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-reset-password"
                >
                  {resettingPassword ? t("pages.users.resetting") : t("pages.users.resetPassword")}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.users.deleteUserConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.users.deleteUserWarning", { name: selectedUser?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              {t("pages.users.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photo Capture Dialog */}
      <Dialog open={isPhotoDialogOpen} onOpenChange={(open) => {
        setIsPhotoDialogOpen(open);
        if (!open && streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
          setCapturing(false);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("pages.users.captureUserPhoto")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!capturedPhoto ? (
              <>
                {capturing && (
                  <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    onClick={capturePhoto}
                    disabled={!capturing}
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    {t("pages.users.capturePhoto")}
                  </Button>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="flex-1"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {t("pages.users.uploadPhoto")}
                  </Button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFilePhoto}
                  className="hidden"
                />
              </>
            ) : (
              <>
                <div className="relative aspect-video bg-slate-900 rounded-lg overflow-hidden">
                  <img
                    src={capturedPhoto}
                    alt={t("pages.users.userPhoto")}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={retakePhoto}
                    variant="outline"
                    className="flex-1"
                  >
                    {t("pages.users.retake")}
                  </Button>
                  <Button
                    onClick={() => {
                      setIsPhotoDialogOpen(false);
                      if (streamRef.current) {
                        streamRef.current.getTracks().forEach(track => track.stop());
                        streamRef.current = null;
                      }
                      setCapturing(false);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {t("common.confirm")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Foto Grande */}
      <Dialog open={isPhotoModalOpen} onOpenChange={setIsPhotoModalOpen}>
        <DialogContent className="max-w-4xl w-[90vw] bg-white p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-2xl font-bold text-slate-900">
              {selectedPhoto?.name || t("pages.users.user")}
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-0">
            {selectedPhoto?.photo ? (
              <div className="flex items-center justify-center bg-slate-100 rounded-lg overflow-hidden">
                <img
                  src={`data:image/jpeg;base64,${selectedPhoto.photo}`}
                  alt={selectedPhoto.name}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-slate-100 rounded-lg">
                <p className="text-slate-400">{t("pages.users.noPhotoAvailable")}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
